import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { formatData, keyCode } from '../../helpers';
import { Checkbox, Icon, textareaToPlainText, Label, SkeletonLoader } from '../../components';
import { useTooltip, useDebounce } from '../../hooks';
import { formatSelectOptions, formatOptionTooltip } from './utils';
import { InlineSelect } from '../Input/components';

import { ValidationMessage } from '../ValidationMessage';
import './Select.css';

/**
 * Filterable dropdown select component with single- and multi-select support.
 *
 * @param {string}   name                    - Form field name; passed back in onChange events.
 * @param {*}        value                   - Selected value (scalar for single, array of {value} for isMulti).
 * @param {Object[]} options                 - Option list; each entry must have at least { value, label }.
 *                                            Optional per-entry extras: `icon` (rendered via formatData(icon,'icon')),
 *                                            `photo`, `className` (styles the label), and `pill`
 *                                            ({ label, value, values, className }) which renders a colored pill in the
 *                                            icon slot via formatData(label,'pill',value,values) — takes precedence over `icon`.
 *                                            `searchText` (string) is an optional plain-text mirror matched by the
 *                                            filter when `label`/`secondaryLabel` is a React node (which can't be searched).
 * @param {Function} onChange                - Called with { name, value, option } on selection change.
 * @param {boolean}  [isNullable=false]      - Shows a clear button that clears the selection. In single-select mode
 *                                            the value is set to null; in multi-select mode all items are toggled off.
 * @param {boolean}  [isClearable=true]      - Shows a clear button that clears active filter text. When filter text
 *                                            is present it takes priority over clearing the selection. Set to false
 *                                            to suppress the button entirely when only filter text is active.
 * @param {boolean}  [isMulti=false]         - Enables multi-select mode; value must be an array of {value} objects.
 *                                            The clear button (when visible) removes all selected items.
 * @param {boolean}  [isOptional=false]      - Renders an "(optional)" label hint next to the field label.
 * @param {boolean}  [sortOptions=true]      - Sorts options alphabetically; prefix matches rank higher.
 * @param {boolean}  [expandedByDefault=true]- Whether the value container starts expanded.
 * @param {boolean}  [showMultiSelectedElements=true] - When false, hides selected pills and shows placeholder.
 * @param {boolean}  [iconAsButton=false]    - Treats the icon as a toggle button that collapses the input.
 * @param {boolean}  [withSelect=false]      - Renders one or more compact inline selects attached to the right of
 *                                            this select, combined into a single bordered box.
 * @param {Object[]} [selectProps]           - Config for each attached inline select when `withSelect` is true. Each
 *                                            entry maps to an `<InlineSelect>`:
 *                                            { name, value, label, tooltip, options, onChange, isMulti, disabled }.
 */
export const Select = ({
  name,
  value,
  label,
  options,
  className,
  onInputChange,
  loadMoreOptions,
  loading,
  resultCount,
  searchTerm,
  clearSearchResults,
  onChange,
  onBlur,
  onFocus,
  onMouseEnterOption,
  onMouseLeaveOption,
  onSelectAll,
  showSearchResultCount = false,
  isNullable = false,
  isClearable = true,
  isMulti = false,
  icon,
  iconAsButton = false,
  expandedByDefault = true,
  title,
  placeholder = 'Type to filter...',
  setFocus = false,
  disabled = false,
  sortOptions = true,
  isOptional = false,
  validation,
  showMultiSelectedElements = true,
  withSelect = false,
  selectProps,
}) => {
  const { showTooltip, hideTooltip } = useTooltip();

  const [_searchTerm, _setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [menuDirection, setMenuDirection] = useState('');
  const [loadMoreData, setLoadMoreData] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(null);

  const refOptionsContainer = useRef();
  const refOptions = useRef([]);
  const refOptionsCheckbox = useRef([]);
  const refInput = useRef();
  const refClearButton = useRef();
  const refToggleMenuButton = useRef();
  const refLoadMoreOptions = useRef();
  const refIcon = useRef();
  const refSelectAll = useRef();
  const refSelectAllCheckbox = useRef();
  const refValueContainer = useRef();

  const filterTerm = searchTerm || _searchTerm;
  const setFilterTerm = _setSearchTerm;

  useLayoutEffect(() => {
    setExpanded(expandedByDefault ? true : false);
  }, [value]);

  useEffect(() => {
    if (loadMoreOptions) {
      const element = refOptionsContainer.current;
      element.addEventListener('scroll', trackScrolling);

      return () => {
        element.removeEventListener('scroll', trackScrolling);
      };
    }
  }, []);

  useEffect(() => {
    if (loadMoreData && loadMoreOptions) {
      loadMoreOptions();
      //setLoadMoreData(prev => {return false})
    }
  }, [loadMoreData]);

  useEffect(() => {
    setLoadMoreData(prev => {
      return false;
    });
  }, [options]);

  useEffect(() => {
    // execute focusInput prop function if provided
    if (setFocus) focusInput();
  }, [setFocus]);

  useEffect(() => {
    // set ref to each option
    refOptions.current = new Array(options.length);
    if (isMulti) {
      refOptionsCheckbox.current = new Array(options.length);
    }
  }, [filterTerm]);

  useLayoutEffect(() => {
    if (!isMulti || !refValueContainer.current || value.length === 0) {
      setVisibleCount(null);
      return;
    }

    const container = refValueContainer.current;
    const style = window.getComputedStyle(container);
    const paddingLeft = parseFloat(style.paddingLeft) || 0;
    const paddingRight = parseFloat(style.paddingRight) || 0;
    const toggleButtonWidth = refToggleMenuButton.current?.offsetWidth || 0;
    const availableWidth = container.clientWidth - paddingLeft - paddingRight - toggleButtonWidth;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = style.font;

    const sortedValue = [...value].sort((a, b) => {
      const ai = options.findIndex(o => String(o.value) === String(a.value));
      const bi = options.findIndex(o => String(o.value) === String(b.value));
      return ai - bi;
    });

    const labels = sortedValue.map(v => {
      if (v.label) return v.label;
      const opt = options.find(o => String(o.value) === String(v.value));
      return opt ? opt.label : String(v.value);
    });

    // Find the maximum number of labels that fit, from full list downward.
    // Always show at least 1 even if it overflows.
    let count = 1;
    for (let i = labels.length; i >= 1; i--) {
      const text =
        i === labels.length
          ? labels.join(', ')
          : labels.slice(0, i).join(', ') + ` +${labels.length - i} more`;
      if (ctx.measureText(text).width <= availableWidth) {
        count = i;
        break;
      }
    }

    setVisibleCount(count);
  }, [value, options, isMulti]);

  useEffect(() => {
    let results = [];

    // Use internal filter function if external is not provided
    if (!searchTerm) {
      results = options.filter(
        option =>
          // label may be a React node (e.g. a method pill + name); only text-match strings.
          (typeof option.label === 'string' &&
            option.label.toLowerCase().includes(filterTerm.toLowerCase())) ||
          // secondaryLabel may be a React node (e.g. a method pill + name); only text-match strings.
          (typeof option.secondaryLabel === 'string' &&
            option.secondaryLabel.toLowerCase().includes(filterTerm.toLowerCase())) ||
          // Optional plain-text mirror for matching when secondaryLabel (or label) is a React node.
          (typeof option.searchText === 'string' &&
            option.searchText.toLowerCase().includes(filterTerm.toLowerCase())) ||
          option.value.toString().toLowerCase().includes(filterTerm.toLowerCase())
      );
      setFilteredOptions(sortResults(results));
    } else {
      setFilteredOptions(options);
    }
  }, [options, filterTerm, showMenu]);

  useLayoutEffect(() => {
    if (showMenu && filterTerm === '') {
      // check on filterTerm because we do not want the dropdown to flip between upwards and downwards when filtering
      setMenuDirection(
        window.innerHeight -
          refInput.current.getBoundingClientRect().top -
          refInput.current.offsetHeight -
          refOptionsContainer.current.offsetHeight <
          0
          ? ' up'
          : ''
      );
    }
  }, [showMenu, filteredOptions]);

  const sortResults = results => {
    // Sort search results
    if (sortOptions) {
      // label may be a React node; sort on its plain-text mirror (searchText) when so.
      const labelText = o =>
        (typeof o.label === 'string' ? o.label : o.searchText || '').toLowerCase();
      const entity = filterTerm.toLowerCase();
      return results.sort((a, b) => {
        if (filterTerm) {
          // If the search term matches the beginning of the option label, give it a higher sort order
          if (labelText(a).indexOf(entity) === 0 && labelText(b).indexOf(entity) !== 0) {
            return -1;
          } else if (labelText(a).indexOf(entity) !== 0 && labelText(b).indexOf(entity) === 0) {
            return 1;
          }
        }

        // Sort normally in asc order if none of the above cases apply
        if (labelText(a) > labelText(b)) {
          return 1;
        } else if (labelText(a) < labelText(b)) {
          return -1;
        } else {
          return 0;
        }
      });
    } else {
      // Sort results is disabled, keep original sort order
      return results;
    }
  };

  const toggleMenu = (mode = undefined) => {
    if (mode === undefined) {
      setShowMenu(!showMenu);
    } else {
      setShowMenu(mode);
    }
  };

  const handleKeyDown = (e, val) => {
    switch (e.keyCode) {
      case keyCode.ENTER:
      case keyCode.SPACE:
        if (val !== undefined) {
          handleSelectOption(val);
        } else {
          toggleMenu(true);
        }
        break;

      case keyCode.DOWN_ARROW:
        focusNextListItem(keyCode.DOWN_ARROW);
        break;

      case keyCode.UP_ARROW:
        focusNextListItem(keyCode.UP_ARROW);
        break;

      case keyCode.ESCAPE:
        toggleMenu(false);
        focusInput();
        break;

      default:
        break;
    }
  };

  const focusNextListItem = direction => {
    const activeElement = document.activeElement;

    const activeElementIndex = refOptions.current.findIndex(el => el === activeElement);
    const activeOptionIndex =
      activeElementIndex < 0 ? options.findIndex(el => el.value === value) : activeElementIndex;

    if (activeOptionIndex >= 0) {
      if (direction === keyCode.DOWN_ARROW && activeOptionIndex + 1 < refOptions.current.length) {
        refOptions.current[activeOptionIndex + 1].focus();
      } else if (direction === keyCode.UP_ARROW && activeOptionIndex > 0) {
        refOptions.current[activeOptionIndex - 1].focus();
      }
    } else {
      refOptions.current[0].focus();
    }
  };

  const focusInput = () => {
    refInput.current.focus();
  };

  const isVisible = el => {
    return el.getBoundingClientRect().top <= window.innerHeight;
  };

  const trackScrolling = useDebounce(() => {
    const loadingElement = refLoadMoreOptions.current;

    if (loadingElement && isVisible(loadingElement)) {
      setLoadMoreData(prev => {
        return true;
      });
    }
  }, 200).debounce;

  const handleInputChange = async event => {
    let value = event.target.value;
    if (value === ' ' && filterTerm === '') {
      // do nothing
    } else {
      setFilterTerm(value);
      refOptionsContainer.current.scrollTop = 0;

      if (onInputChange) {
        onInputChange(value);
      }
    }
    toggleMenu(true);
  };

  /**
   * Handles the clear button click with the following priority:
   * 1. If filter text is active, clear it only — never touch the selection.
   * 2. If isMulti, toggle off each selected item via onChange so the parent can update state.
   * 3. Otherwise fall through to single-select nullable/clearable behaviour.
   */
  const handleClearSelection = () => {
    if (filterTerm) {
      setFilterTerm('');
      refInput.current.focus();
      if (clearSearchResults) clearSearchResults();
      return;
    }
    if (isMulti && isNullable) {
      value.forEach(item => onChange({ name, value: item.value }));
      focusInput();
      return;
    }
    if (isNullable) {
      handleSelectOption(null);
    } else if (isClearable) {
      setFilterTerm('');
      refInput.current.focus();
      if (clearSearchResults) clearSearchResults();
    }
  };

  const handleSelectOption = val => {
    if (isMulti) {
      onChange({ name: name, value: val?.value || '', option: val });
    } else {
      toggleMenu(false);
      onChange({ name: name, value: val?.value || '', option: val });

      if (clearSearchResults) {
        // execute clearSearchResults prop function if provided
        clearSearchResults();
      }

      if (iconAsButton) {
        setExpanded(false);
      } else {
        setFilterTerm('');
        focusInput();
      }
    }
  };

  const handleSelectAll = e => {
    e.preventDefault();
    onSelectAll && onSelectAll();
    focusInput();
  };

  const handleSelectCheckbox = event => {
    // Strip "selected-" prefix and find the matching option to get its correctly-typed value
    const rawValue = event.target.name.slice('selected-'.length);
    const option = filteredOptions.find(o => String(o.value) === rawValue);

    // add item to selection array
    onChange({ name: name, value: option ? option.value : rawValue });
  };

  const handleBlur = e => {
    const activeElement = e.relatedTarget;

    if (
      activeElement === null ||
      !(
        activeElement === refInput.current ||
        activeElement === refToggleMenuButton.current ||
        activeElement === refIcon.current ||
        activeElement === refClearButton.current ||
        activeElement === refLoadMoreOptions.current ||
        activeElement === refSelectAll.current ||
        activeElement === refSelectAllCheckbox.current ||
        refOptions.current.findIndex(el => el === activeElement) >= 0 ||
        (refOptionsCheckbox &&
          refOptionsCheckbox.current.findIndex(el => el === activeElement) >= 0)
      )
    ) {
      toggleMenu(false);

      if (clearSearchResults) {
        // execute clearSearchResults prop function if provided
        //clearSearchResults()
      }

      if (onBlur) {
        // execute onBlur prop function if provided
        onBlur();
      }

      if (iconAsButton) {
        setExpanded(false);
      }

      if (!iconAsButton) {
        setFilterTerm('');
      }
    }
  };

  const handleMouseEnter = (e, option) => {
    if (onMouseEnterOption) onMouseEnterOption(e, option);
    e.preventDefault();

    showTooltip(formatOptionTooltip(option));
  };
  const handleMouseLeave = (e, option) => {
    e.preventDefault();
    hideTooltip();
    if (onMouseLeaveOption) onMouseLeaveOption(e, option);
  };

  const handleExpand = el => {
    onFocus && onFocus(el);

    refInput.current.focus();
    setExpanded(true);
  };

  const optionIsSelected = match => {
    if (isMulti) {
      return value.find(x => x.value === match) ? true : false;
    } else {
      return value === match ? true : false;
    }
  };

  const getOptionValue = val => {
    return val !== undefined && val !== null && options.length > 0
      ? options.find(option => option.value.toString() === val.toString())
      : undefined;
  };

  const getOptionLabel = val => {
    const res = getOptionValue(val);

    if (res !== undefined) {
      let elements = [];
      let icon;
      let photo;

      // A pill (e.g. an API method badge) takes the icon slot when present; otherwise the icon.
      if (res.pill) {
        elements.push(
          <div
            key="pill"
            className={'select-option-pill' + (res.pill.className ? ' ' + res.pill.className : '')}
          >
            {formatData(res.pill.label, 'pill', res.pill.value, res.pill.values)}
          </div>
        );
      } else if (res.icon) {
        elements.push(
          <div key="icon" className="select-option-icon">
            {' '}
            {formatData(res.icon, 'icon')}
          </div>
        );
      } else if (res.environment) {
        elements.push(
          <div key="environment" className={'environment-label ' + res.environment}>
            <span>{res.environment}</span>
          </div>
        );
      }
      if (res.hasOwnProperty('photo')) {
        elements.push(
          <div key="photo" className="select-option-photo">
            {' '}
            {formatData(res.label, 'owner', undefined, undefined, undefined, { photo: res.photo })}
          </div>
        );
      }

      elements.push(
        <div
          key="label"
          className={'select-value-label' + (res.className ? ' ' + res.className : '')}
        >
          {res.label}
        </div>
      );

      return <div className="select-value">{elements}</div>;
    }
    return;
  };

  const inputShowTooltip = () => {
    const label = getOptionValue(value)?.label;
    if (title) {
      showTooltip({ title: title });
    } else if (label) {
      showTooltip({ title: label });
    }
  };

  const selectOptionsDirection = menuDirection;
  const menuState = showMenu && (filteredOptions.length > 0 || loading || searchTerm) ? 'show' : '';
  const noValue = isMulti
    ? !showMultiSelectedElements || value.length === 0
    : value === '' || value === undefined || value === null;

  const getMultiLabel = () => {
    if (value.length === 0) return placeholder;
    const sortedValue = [...value].sort((a, b) => {
      const ai = options.findIndex(o => String(o.value) === String(a.value));
      const bi = options.findIndex(o => String(o.value) === String(b.value));
      return ai - bi;
    });
    const labels = sortedValue.map(v => {
      if (v.label) return v.label;
      const opt = options.find(o => String(o.value) === String(v.value));
      return opt ? opt.label : String(v.value);
    });
    const count = visibleCount !== null ? visibleCount : labels.length;
    if (count >= labels.length) return labels.join(', ');
    return labels.slice(0, count).join(', ') + ` +${labels.length - count} more`;
  };

  const selectLabel =
    filterTerm === ''
      ? isMulti
        ? showMultiSelectedElements
          ? getMultiLabel()
          : placeholder
        : noValue
          ? placeholder
          : getOptionLabel(value)
      : '';
  const allIsSelected =
    (onSelectAll || showSearchResultCount) &&
    filteredOptions &&
    filteredOptions.length > 0 &&
    filteredOptions.filter(x => {
      return optionIsSelected(x.value);
    }).length === filteredOptions.length
      ? true
      : false;

  // The options dropdown. Rendered inside .select-container by default, but at the
  // .select-container-wrapper level when withSelect, so it can span the full width of the
  // combined control instead of only the main (left) half. See Select.css `.with-select`.
  const optionsMenu = (
    <div className={'select-options'} ref={refOptionsContainer} role="listbox">
      {filteredOptions.length > 0 ? (
        <React.Fragment>
          {(onSelectAll || showSearchResultCount) && (
            <div className="select-options-header">
              {onSelectAll && (
                <div
                  className="button-select-all"
                  onClick={e => {
                    handleSelectAll(e);
                  }}
                  ref={refSelectAll}
                  tabIndex={0}
                >
                  <Checkbox
                    className=""
                    name=""
                    label="(All)"
                    title={allIsSelected ? 'De-select all' : 'Select all'}
                    ref={refSelectAllCheckbox}
                    onChange={handleSelectCheckbox}
                    checked={allIsSelected}
                  />
                </div>
              )}
              {showSearchResultCount && (
                <div className="select-options-count">{resultCount} assets found</div>
              )}
            </div>
          )}
          {filteredOptions.map((option, i) => (
            <div
              className={
                'select-option' +
                //(optionIsSelected(option.value) || (option.value && option.disabled) ? " active": "") +
                (optionIsSelected(option.value) ? ' active' : '') +
                (option.secondaryLabel === undefined ? '' : ' narrow-padding') +
                //(option.selectedByOther || (option.value && option.disabled) ? " selected" : "") +
                (option.selectedByOther ? ' selected' : '') +
                (option.disabled ? ' disabled' : '')
              }
              key={option.value}
              ref={el => (refOptions.current[i] = el)}
              aria-label={typeof option.label === 'string' ? option.label : option.searchText}
              aria-selected={optionIsSelected(option.value)}
              role="option"
              tabIndex={0}
              onClick={e => {
                e.preventDefault();
                !option.disabled && handleSelectOption(option);
              }}
              onKeyDown={e => !option.disabled && handleKeyDown(e, option)}
              onMouseEnter={e => handleMouseEnter(e, option)}
              onMouseLeave={e => handleMouseLeave(e, option)}
              onBlur={handleBlur}
            >
              {isMulti && (
                <div className="select-option-checkbox">
                  <Checkbox
                    className={option.selectedByOther ? 'dim-selected' : ''}
                    name={`selected-${option.value}`}
                    ref={el => (refOptionsCheckbox.current[i] = el)}
                    onChange={handleSelectCheckbox}
                    checked={(() => {
                      const found = isMulti ? value.find(x => x.value === option.value) : null;
                      if (found) return found.partial ? 'multiple' : true;
                      if (option.value && option.disabled) return true;
                      return false;
                    })()}
                    disabled={option.disabled ? true : false}
                  />
                </div>
              )}

              {/* A pill (e.g. an API method badge) takes the icon slot when present; else the icon. */}
              {option.pill ? (
                <div
                  className={
                    'select-option-pill' +
                    (option.pill.className ? ' ' + option.pill.className : '')
                  }
                >
                  {formatData(option.pill.label, 'pill', option.pill.value, option.pill.values)}
                </div>
              ) : (
                option.icon && (
                  <div className="select-option-icon">{formatData(option.icon, 'icon')}</div>
                )
              )}
              {option.hasOwnProperty('environment') && (
                <div key="environment" className={'environment-label ' + option.environment}>
                  <span>{option.environment}</span>
                </div>
              )}

              {option.hasOwnProperty('photo') && (
                <div className="select-option-photo">
                  {formatData(option.label, 'owner', undefined, undefined, undefined, {
                    photo: option.photo,
                  })}
                </div>
              )}
              <div className="select-option-label">
                <div className={option.className ? ' ' + option.className : ''}>{option.label}</div>
                <div className="select-option-secondary-label">{option.secondaryLabel}</div>
              </div>
            </div>
          ))}
        </React.Fragment>
      ) : searchTerm === undefined ? (
        filterTerm.length > 0 && <div className="select-options-empty">No results</div>
      ) : (
        filteredOptions.length === 0 &&
        !loading &&
        filterTerm.length > 1 && <div className="select-options-empty">No results</div>
      )}

      {((loading && filteredOptions.length === 0) ||
        (resultCount && filteredOptions.length < resultCount && filteredOptions.length > 0)) && (
        <div
          className="select-option"
          ref={refLoadMoreOptions}
          onClick={() =>
            setLoadMoreData(prev => {
              return true;
            })
          }
          title="Click to load more items"
        >
          <div className="select-option-label">
            <div>
              <SkeletonLoader width="200px" />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={
        'CustomSelect' +
        (icon ? ' with-icon' : '') +
        (iconAsButton ? ' icon-as-button' : '') +
        (iconAsButton && !expanded ? ' collapsed' : '') +
        (disabled ? ' disabled' : '') +
        (className ? ' ' + className : '') +
        (validation && !validation.valid ? ' has-error' : '') +
        (withSelect ? ' with-select' : '')
      }
    >
      <div className="select-container-wrapper">
        {/* Main select (label + dropdown). Grouped so an inline select can sit beside it when withSelect. */}
        <div className="select-main">
          <Label
            label={label}
            htmlFor={name}
            title={title}
            disabled={disabled}
            isOptional={isOptional}
            onClick={() => {
              focusInput();
              toggleMenu();
            }}
          />
          <div className={'select-container ' + menuState + selectOptionsDirection}>
            <div
              className="select-input-container"
              onMouseEnter={inputShowTooltip}
              onMouseLeave={() => title && hideTooltip()}
            >
              {!disabled && (
                <div className="select-button-container">
                  {(isMulti
                    ? (isNullable && value.length > 0) || (isClearable && !!filterTerm)
                    : (isNullable && value) || (isClearable && !!filterTerm)) && (
                    <div
                      className="select-button button-clear-selection"
                      onClick={handleClearSelection}
                      ref={refClearButton}
                      tabIndex={1}
                    >
                      <div className="button-clear-selection-icon"></div>
                    </div>
                  )}
                  {expanded && (filteredOptions.length > 0 || loading || searchTerm) && (
                    <div
                      className="select-button button-toggle-menu"
                      onClick={() => {
                        focusInput();
                        toggleMenu();
                      }}
                      ref={refToggleMenuButton}
                      tabIndex={1}
                    >
                      <div className="button-toggle-menu-icon"></div>
                    </div>
                  )}
                </div>
              )}
              {icon && (
                <div
                  className="input-icon"
                  ref={refIcon}
                  onClick={() => {
                    focusInput();
                    toggleMenu();
                  }}
                >
                  <Icon name={icon} tooltip={title} />
                </div>
              )}
              <input
                type="text"
                className="select-input"
                tabIndex={0}
                //aria-expanded={ showMenu }
                aria-haspopup="listbox"
                aria-autocomplete="list"
                ref={refInput}
                name={name}
                value={filterTerm}
                disabled={disabled}
                autoComplete="off" // prevent browsers from autofilling
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onClick={() => toggleMenu()}
                onBlur={handleBlur}
                onFocus={handleExpand}
              />
            </div>

            <div
              ref={refValueContainer}
              className={
                'select-value-container' +
                (noValue ? ' placeholder' : '') +
                (disabled ? ' disabled' : '')
              }
            >
              {selectLabel}
            </div>

            {/* Default placement: dropdown anchored to the main select half. */}
            {!withSelect && optionsMenu}
          </div>
        </div>
        {/* Inline select(s) combined into the same bordered box as the main select. */}
        {withSelect && selectProps && (
          <div className="select-buttons">
            {selectProps.map((props, i) => (
              <InlineSelect
                key={i}
                name={props.name}
                value={props.value}
                label={props.label}
                tooltip={props.tooltip}
                options={props.options}
                onChange={props.onChange}
                isMulti={props.isMulti}
                disabled={props.disabled ?? disabled}
                inputRef={refInput}
              />
            ))}
          </div>
        )}
        {/* withSelect: dropdown anchored to the full-width wrapper so it spans the whole
            combined control instead of only the main (left) half. */}
        {withSelect && optionsMenu}
      </div>
      <ValidationMessage validation={validation} />
    </div>
  );
};
